import { CircularProgress } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import usePostAdyenApplePayment from "../../../apis/usePostAdyenApplePayment";
import usePostAdyenGooglePayment from "../../../apis/usePostAdyenGooglePayment/usePostAdyenGooglePayment";
import { ADYEN_RESULT_CODE } from "../../../apis/useSubmitPayment/types";
import useSubmitPayment from "../../../apis/useSubmitPayment/useSubmitPayment";
import Condition from "../../../components/Conditional/Condition";
import Overlay from "../../../components/Overlay/Overlay";
import PaymentErrorDialog from "../../../components/dialogs/PaymentErrorDialog";
import { useCartActions } from "../../../contexts/CartServiceContext/CartServiceContext";
import { usePaymentContext } from "../../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../../router/path";
// Order matters: the scoped reset must be injected before the vendored Adyen sheet so
// that on equal-specificity ties Adyen's own declarations win (later sheet wins ties).
import "./AdyenCheckoutForm.scss";
// The official stylesheet, from the package — so it always matches the installed SDK. The
// repo used to carry a hand-vendored copy, which drifted (older build, hand-edited spacing);
// its one deliberate edit lives on as a named override in AdyenCheckoutForm.scss.
import "@adyen/adyen-web/styles/adyen.css";
import { useAdyenCard } from "../useAdyenCard";

export const PREV_PAYMENT_REFERENCE = "PREV_PAYMENT_REFERENCE";
interface IAdyenCheckoutFormProps {
	isMixingPayment?: boolean;
}

const AdyenCheckoutForm = (props: IAdyenCheckoutFormProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const cardContainer = useRef(null);
	const applePayContainer = useRef(null);
	const googlePayContainer = useRef(null);
	const { state: paymentReference } = usePaymentContext((state) => state?.paymentReference);
	const { submitApplePayment } = usePostAdyenApplePayment();
	const { submitGooglePayment } = usePostAdyenGooglePayment();
	const [isLoading, setIsLoading] = useState(false);
	const [paymentError, setPaymentError] = useState<string | null>(null);

	const handlePaymentError = useCallback(
		(error: any) => {
			setIsLoading(false);
			let errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				"An unexpected error occurred during payment processing.";

			// Check if message is a translation key
			const translationKeys = [
				"paymentPage.error.cardRefused",
				"paymentPage.error.3dsAuthenticationFailed",
			];

			if (translationKeys.includes(errorMessage)) {
				errorMessage = t(errorMessage);
			}

			setPaymentError(errorMessage);
		},
		[t]
	);

	const { submitPayment } = useSubmitPayment(handlePaymentError);
	const handlePaymentSubmit = useCallback(
		(paymentData: any, _component: any, actions: any) => {
			setIsLoading(true);
			submitPayment(paymentData, actions);
		},
		[submitPayment]
	);

	const { CLEAN_CART } = useCartActions();

	const handlePaymentCompleted = useCallback(
		(_result: any) => {
			setIsLoading(false);
			setPaymentError(null);
			CLEAN_CART();
			navigate(`${PATH.ORDER_CONFIRMATION}?merchantReference=${paymentReference}`, {
				replace: false,
			});
		},
		[paymentReference, navigate, CLEAN_CART]
	);

	const handleAdditionalDetails = useCallback(
		(state: any, component: any) => {
			setIsLoading(true);

			// Handle 3D Secure authentication result
			if (state.data?.details) {
				submitPayment(state.data, {
					resolve: (result: any) => {
						setIsLoading(false);
						if (result.resultCode === ADYEN_RESULT_CODE.AUTHORISED) {
							handlePaymentCompleted(result);
						} else {
							// 3D Secure authentication failed
							handlePaymentError({
								response: {
									data: {
										message: "paymentPage.error.3dsAuthenticationFailed",
									},
								},
							});
						}
					},
					reject: (error: any) => {
						setIsLoading(false);
						handlePaymentError(error);
					},
				});
			}
		},
		[submitPayment, handlePaymentCompleted, handlePaymentError]
	);

	const handleErrorDialogClose = useCallback(() => {
		setPaymentError(null);
		navigate(PATH.CART, { replace: true });
	}, [navigate]);

	const { isCardValid, submit } = useAdyenCard({
		cardContainerRef: cardContainer,
		onPaymentSubmit: handlePaymentSubmit,
		onPaymentCompleted: handlePaymentCompleted,
		onAdditionalDetails: handleAdditionalDetails,
		applePayContainerRef: applePayContainer,
		googlePayContainerRef: googlePayContainer,
		onApplePayPayment: submitApplePayment,
		onGooglePayPayment: submitGooglePayment,
	});

	return (
		<div className="AdyenCheckoutForm">
			<Condition check={isLoading}>
				<Overlay />
			</Condition>

			<Condition check={props.isMixingPayment}>
				<div className="AdyenDiscountAlert">
					<p>{t("paymentPage.alert.noLinkPayDiscount")}</p>
				</div>
			</Condition>

			<div id="applepay-container" className="AdyenScope" ref={applePayContainer} />
			<div id="googlepay-container" className="AdyenScope" ref={googlePayContainer} />

			<div className="CardContainer AdyenScope" ref={cardContainer} />
			<br />

			<button
				data-testid="confirm-payment-button"
				className={`ConfirmPaymentButton ${isCardValid ? "Enabled" : "Disabled"}`}
				disabled={!isCardValid || isLoading}
				onClick={submit}
			>
				<Condition check={isLoading}>
					<CircularProgress size={24} />
				</Condition>
				<Condition check={!isLoading}>
					<p>{t("paymentPage.confirmAndPay")}</p>
				</Condition>
			</button>

			<PaymentErrorDialog
				open={!!paymentError}
				errorMessage={paymentError || ""}
				onClose={handleErrorDialogClose}
			/>
		</div>
	);
};

export default AdyenCheckoutForm;
